import React from 'react';
import { Box, Typography, Popover, Radio, RadioGroup, FormControlLabel, Button, Divider } from '@mui/material';
import { ArrowDownAZ, ArrowUpAZ } from 'lucide-react';
import { FilterParams } from '../transactions-table';

export type BankFilterHandle = {
    applyNow: () => void;
    clear: () => void;
};

interface BankFilterProps {
    anchorEl?: HTMLElement | null;
    open?: boolean;
    onClose?: () => void;
    onApply: (params: FilterParams) => React.SetStateAction<void>;
    currentSort?: {
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    };
    // Renders the same sort controls inline (no Popover/anchor) for use
    // inside a combined modal instead of an anchored popup.
    embedded?: boolean;
    // Hides this section's own Apply Now/Clear buttons - used when a
    // parent (the combined filter modal) drives Apply/Clear for every
    // section at once via the imperative handle below instead.
    hideActions?: boolean;
}

const BankFilter = React.forwardRef<BankFilterHandle, BankFilterProps>(({
    anchorEl, open, onClose,
    onApply,
    currentSort,
    embedded = false,
    hideActions = false,
}, ref) => {
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | ''>(
        currentSort?.sortBy === 'bank' ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : ''
    );

    // Update state when props change
    React.useEffect(() => {
        setSortOrder(currentSort?.sortBy === 'bank' ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : '');
    }, [currentSort]);

    const handleClear = () => {
        setSortOrder('');
        // Clear bank filter but default sorting to date
        onApply({
            sortBy: 'date',
            sortDirection: 'DESC',
        });
    }

    const handleApplyNow = () => {
        const params: any = {};
        if (sortOrder) {
            params.sortBy = 'bank';
            params.sortDirection = sortOrder.toUpperCase() as 'ASC' | 'DESC';
        }
        onApply(params);
    };

    React.useImperativeHandle(ref, () => ({
        applyNow: handleApplyNow,
        clear: handleClear,
    }));

    const content = (
        <>
            {!embedded && <>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort</Typography>
                <Divider sx={{ my: 1 }}/>
            </>}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: 14 }}>Sort By Bank</Typography>
                </Box>
            </Box>
            <RadioGroup value={sortOrder || ''} onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')} sx={{ mb: 1, pl: 3 }}>
                <FormControlLabel
                    value="asc"
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>}
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowDownAZ size={14} />Sort A to Z</Typography>}
                />
                <FormControlLabel
                    value="desc"
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>}
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowUpAZ size={14} />Sort Z to A</Typography>}
                />
            </RadioGroup>
            {!hideActions && (
                <Box display="flex" gap={1} mt={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-primary)' } }}
                        onClick={handleApplyNow}
                    >
                        Apply Now
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-container-background)' } }}
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                </Box>
            )}
        </>
    );

    if (embedded) {
        return <Box>{content}</Box>;
    }

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: 3, minWidth: 250, p: 2, boxShadow: 3 } }}
        >
            {content}
        </Popover>
    );
});

export default BankFilter;